import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Upload, FileText, CheckCircle, X, AlertCircle, Download, Eye, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import * as documentService from '../../services/documentService';
import { Document } from '../../services/documentService';
import { getUserData } from '../../utils/authUtils';

const UploadDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [expandedTimelines, setExpandedTimelines] = useState<Set<number>>(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);


  const userId = getUserData()?.userId ?? 0;

  // Generar años (desde 2020 hasta año actual + 1)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);
  const hasRejectedLastVersion = useMemo(() => {
    const docsFiltradosByPeriodo = documents.filter(d => d.year === selectedYear && d.month === selectedMonth);
    const lastVersion = Math.max(...docsFiltradosByPeriodo.map(d => d.version), 0);
    return docsFiltradosByPeriodo.some(doc => doc.status === 'rechazado' && doc.version === lastVersion);
  }, [documents, selectedYear, selectedMonth]);

  // Meses
  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Obtener reportes del usuario autenticado (userId resuelto por el backend via JWT)
      const response = await documentService.getMyClientReports();
      setDocuments(response.documents);
    } catch (err) {
      console.error('Error al cargar documentos:', err);
      setError('Error al cargar documentos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Validar archivos antes de seleccionarlos
      const files = Array.from(e.target.files);
      const invalidFiles = files.filter(file => !documentService.isValidFileType(file));
      const oversizedFiles = files.filter(file => !documentService.isValidFileSize(file));

      if (invalidFiles.length > 0) {
        setError('Algunos archivos tienen un formato no válido. Solo se permiten PDF, JPG, PNG');
        e.target.value = '';
        return;
      }

      if (oversizedFiles.length > 0) {
        setError('Algunos archivos superan el tamaño máximo de 10MB');
        e.target.value = '';
        return;
      }

      setSelectedFiles(e.target.files);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setError('Por favor selecciona al menos un archivo');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Subir cada archivo secuencialmente usando el endpoint de reportes de cliente
      const uploadPromises = Array.from(selectedFiles).map(file =>
        documentService.uploadClientReport(file, userId, selectedYear, selectedMonth)
      );

      await Promise.all(uploadPromises);

      // Recargar la lista de documentos
      await loadDocuments();

      setSuccessMessage(
        `${selectedFiles.length} reporte(s) subido(s) exitosamente para ${documentService.formatPeriod(selectedYear, selectedMonth)} (pendiente de aprobación)`
      );
      setSelectedFiles(null);

      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Limpiar mensaje de éxito después de 5 segundos
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (err) {
      console.error('Error al subir archivos:', err);
      const message = err instanceof Error ? err.message : 'Error al subir archivos. Por favor, intenta de nuevo.';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      await documentService.downloadDocument(doc.id, doc.originalFileName);
    } catch (err) {
      console.error('Error al descargar:', err);
      setError('Error al descargar el documento');
    }
  };

  const handleView = async (doc: Document) => {
    try {
      await documentService.viewDocument(doc.id);
    } catch (err) {
      console.error('Error al visualizar:', err);
      setError('Error al visualizar el documento');
    }
  };

  const handleDelete = (id: number) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (deleteConfirmId === null) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);

    try {
      await documentService.deleteDocument(id);
      setSuccessMessage('Documento eliminado exitosamente');
      await loadDocuments();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al eliminar el documento';
      setError(message);
    }
  };

  const getEstadoBadge = (estado: string) => {
    switch (estado) {
      case 'aprobado':
        return (
          <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-medium">
            <CheckCircle size={16} />
            Aprobado
          </span>
        );
      case 'rechazado':
        return (
          <span className="flex items-center gap-1 text-red-700 bg-red-100 px-3 py-1 rounded-full text-sm font-medium">
            <X size={16} />
            Rechazado
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full text-sm font-medium">
            <AlertCircle size={16} />
            Pendiente
          </span>
        );
    }
  };

  const documentsByPeriod = useMemo(() => {
    const grouped: { [key: string]: Document[] } = {};
    documents.forEach(doc => {
      const periodKey = `${doc.year}-${doc.month}`;
      if (!grouped[periodKey]) grouped[periodKey] = [];
      grouped[periodKey].push(doc);
    });
    return Object.entries(grouped).sort((a, b) => {
      const [yearA, monthA] = a[0].split('-').map(Number);
      const [yearB, monthB] = b[0].split('-').map(Number);
      if (yearA !== yearB) return yearB - yearA;
      return monthB - monthA;
    });
  }, [documents]);

  const togglePeriod = (periodKey: string) => {
    setExpandedPeriods(prev => {
      const newSet = new Set(prev);
      if (newSet.has(periodKey)) {
        newSet.delete(periodKey);
      } else {
        newSet.add(periodKey);
      }
      return newSet;
    });
  };

  const toggleTimeline = (docId: number) => {
    setExpandedTimelines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <Upload className="text-blue-600" size={28} />
        Mis Reportes Mensuales
      </h2>

      {/* Mensajes de error y éxito */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={20} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} />
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto">
            <X size={20} />
          </button>
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Subir Nuevo Reporte</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Año
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mes
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seleccionar Archivo(s)
            </label>
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              disabled={uploading}
              className="w-full px-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer disabled:opacity-50"
            />
          </div>
        </div>

        {selectedFiles && selectedFiles.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              {selectedFiles.length} archivo(s) seleccionado(s) para{' '}
              <strong>{documentService.formatPeriod(selectedYear, selectedMonth)}</strong>:
            </p>
            <ul className="text-sm text-gray-700">
              {Array.from(selectedFiles).map((file, index) => (
                <li key={index} className="flex items-center gap-2">
                  <FileText size={16} className="text-blue-600" />
                  {file.name} ({documentService.formatFileSize(file.size)})
                </li>
              ))}
            </ul>
          </div>
        )}
{hasRejectedLastVersion && (
  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
    <AlertCircle size={16} className="flex-shrink-0" />
    <span>Existe una versión rechazada para este período. Puedes subir una versión corregida.</span>
  </div>
)}

        <button
          onClick={handleUpload}
          disabled={!selectedFiles || uploading}
          className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition font-semibold flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Subiendo...
            </>
          ) : (
            <>
              <Upload size={20} />
              Subir Reporte
            </>
          )}
        </button>

        <div className="mt-4 p-3 bg-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Nota:</strong> El reporte quedará pendiente de aprobación por Imageclec.
            Formatos aceptados: PDF, JPG, PNG. Tamaño máximo: 10 MB por archivo.
          </p>
        </div>
      </div>

      {/* Documents List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Mis Reportes Subidos</h3>

        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                    <div className="h-5 bg-gray-200 rounded w-32" />
                    <div className="h-5 bg-gray-200 rounded-full w-16" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText size={48} className="mx-auto mb-2 opacity-50" />
            <p>No hay reportes subidos aún</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documentsByPeriod.map(([periodKey, periodDocs]) => {
              const isExpanded = expandedPeriods.has(periodKey);
              const [year, month] = periodKey.split('-').map(Number);
              const periodLabel = documentService.formatPeriod(year, month);

              return (
                <div key={periodKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Fila de encabezado del periodo - clickeable */}
                  <button
                    onClick={() => togglePeriod(periodKey)}
                    className="w-full flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 px-4 py-3 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown size={20} className="text-blue-600" />
                      ) : (
                        <ChevronRight size={20} className="text-blue-600" />
                      )}
                      <span className="text-lg font-semibold text-gray-800">{periodLabel}</span>
                      <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">
                        {periodDocs.length} reporte{periodDocs.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {periodDocs.some(d => d.status === 'aprobado') && (
                        <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          Aprobado
                        </span>
                      )}
                      {periodDocs.some(d => d.status === 'rechazado') && (
                        <span className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded-full">
                          Rechazado
                        </span>
                      )}
                      {periodDocs.some(d => d.status === 'pendiente') && (
                        <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                          Pendiente
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Contenido expandible */}
                  {isExpanded && (
                    <div className="bg-white">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-t border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Versión</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Archivo</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha Subida</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tamaño</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Estado</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {periodDocs.map((doc) => (
                              <React.Fragment key={doc.id}>
                                <tr className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                                    v{doc.version}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <FileText size={18} className="text-blue-600 flex-shrink-0" />
                                      <span className="text-sm text-gray-900">{doc.originalFileName}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {new Date(doc.uploadedAt).toLocaleDateString('es-CL', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {documentService.formatFileSize(doc.fileSize)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      {getEstadoBadge(doc.status)}
                                      <button onClick={() => toggleTimeline(doc.id)} className="text-gray-500 hover:text-gray-700 transition p-1 rounded hover:bg-gray-100" title="Ver historial">
                                      {expandedTimelines.has(doc.id) ? <ChevronDown size={16} /> : <Clock size={16} />}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleView(doc)}
                                        className="text-blue-600 hover:text-blue-800 transition p-1 rounded hover:bg-blue-50"
                                        title="Ver documento"
                                      >
                                        <Eye size={20} />
                                      </button>
                                      <button
                                        onClick={() => handleDownload(doc)}
                                        className="text-green-600 hover:text-green-800 transition p-1 rounded hover:bg-green-50"
                                        title="Descargar documento"
                                      >
                                        <Download size={20} />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(doc.id)}
                                        className="text-red-600 hover:text-red-800 transition p-1 rounded hover:bg-red-50"
                                        title="Eliminar"
                                      >
                                        <X size={20} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                {expandedTimelines.has(doc.id) && (
                                <tr>
                                  <td colSpan={6}>
                                    <div className="flex items-center gap-6 px-4 py-3">
                                      <div className="flex items-center gap-1">
<Upload size={14} className="text-gray-500" />
                                        <span className="text-xs text-gray-500 ml-1">
                                          Subido el {new Date(doc.uploadedAt).toLocaleString('es-CL')}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
{}                                        {doc.status === 'aprobado' && (
                                          <>
                                            <CheckCircle size={14} className="text-green-500" />
                                            <span className="text-xs text-gray-500 ml-1">
                                          Aprobado el {new Date(doc.reviewedAt!).toLocaleString('es-CL')}
                                        </span>
                                          </>
                                          
                                        )}
                                        {doc.status === 'rechazado' && (
                                          <>
                                            <X size={14} className="text-red-500" />
                                            <span className="text-xs text-gray-500 ml-1">
                                          Rechazado el {new Date(doc.reviewedAt!).toLocaleString('es-CL')}
                                        </span>
                                          </>
                                        )}
                                        {doc.status === 'pendiente' && (
                                          <>
                                            <AlertCircle size={14} className="text-yellow-500" />
                                            <span className="text-xs text-gray-500 ml-1">
                                          Pendiente de revisión
                                        </span>
                                          </>
                                        )}

                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                )}
                                {/* Mostrar comentarios del administrador (rechazo o aprobación con observación) */}
                                {doc.comments && (
                                  <tr className={doc.status === 'rechazado' ? 'bg-red-50' : 'bg-green-50'}>
                                    <td colSpan={6} className="px-4 py-3">
                                      <div className="flex items-start gap-2">
                                        {doc.status === 'rechazado' ? (
                                          <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                                        ) : (
                                          <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                                        )}
                                        <div className="flex-1">
                                          <p className={`text-sm font-semibold ${doc.status === 'rechazado' ? 'text-red-800' : 'text-green-800'}`}>
                                            {doc.status === 'rechazado' ? 'Motivo del rechazo:' : 'Observación del administrador:'}
                                          </p>
                                          <p className={`text-sm mt-1 ${doc.status === 'rechazado' ? 'text-red-700' : 'text-green-700'}`}>
                                            {doc.comments}
                                          </p>
                                          {doc.status === 'rechazado' && (
                                            <p className="text-xs text-red-600 mt-1">
                                              Puedes subir una nueva versión corrigiendo los problemas mencionados.
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {deleteConfirmId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Eliminar documento</h3>
            <p className="text-gray-600 mb-6">
              Esta acción no se puede deshacer. ¿Confirmas la eliminación del documento?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDocuments;
